import type { PaginatedResponse } from '@/types'

// API Configuration
const API_BASE_URL = (import.meta as any).env?.VITE_API_BASE_URL || '/api'
const DEFAULT_TIMEOUT = 30000

// HTTP Methods
type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

// Request Options
interface RequestOptions {
  method?: HttpMethod
  headers?: Record<string, string>
  body?: any
  timeout?: number
}

// API Response wrapper
interface ApiResponseWrapper<T> {
  data: T
  status: number
  statusText: string
  headers: Headers
}

class ApiClient {
  private baseURL: string
  private defaultHeaders: Record<string, string>

  constructor(baseURL: string) {
    this.baseURL = baseURL
    this.defaultHeaders = {
      'Content-Type': 'application/json',
    }
  }

  public async request<T>(
    endpoint: string,
    options: RequestOptions = {}
  ): Promise<ApiResponseWrapper<T>> {
    const {
      method = 'GET',
      headers = {},
      body,
      timeout = DEFAULT_TIMEOUT
    } = options

    const url = `${this.baseURL}${endpoint}`
    
    // Prepare headers
    const requestHeaders = {
      ...this.defaultHeaders,
      ...headers
    }

    // Prepare request config
    const requestConfig: RequestInit = {
      method,
      headers: requestHeaders,
      credentials: 'include', // Include cookies for authentication
    }

    // Add body for non-GET requests
    if (body && method !== 'GET') {
      if (body instanceof FormData) {
        // Remove Content-Type header for FormData (browser will set it)
        delete requestHeaders['Content-Type']
        requestConfig.body = body
      } else {
        requestConfig.body = JSON.stringify(body)
      }
    }

    // Add timeout
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), timeout)
    requestConfig.signal = controller.signal

    try {
      const response = await fetch(url, requestConfig)
      clearTimeout(timeoutId)

      // Parse response
      let data: T
      const contentType = response.headers.get('content-type')
      
      if (contentType && contentType.includes('application/json')) {
        data = await response.json()
      } else {
        data = await response.text() as unknown as T
      }

      // Handle HTTP errors
      if (!response.ok) {
        const errorMessage = (data as any)?.message || response.statusText
        throw new ApiError(errorMessage, response.status, data)
      }

      return {
        data,
        status: response.status,
        statusText: response.statusText,
        headers: response.headers
      }
    } catch (error) {
      clearTimeout(timeoutId)
      
      if (error instanceof ApiError) {
        throw error
      }
      
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          throw new ApiError('Request timeout', 408)
        }
        throw new ApiError(error.message, 0)
      }
      
      throw new ApiError('Network error', 0)
    }
  }

  // HTTP Methods
  async get<T>(endpoint: string, params?: Record<string, any>): Promise<ApiResponseWrapper<T>> {
    let url = endpoint
    
    if (params) {
      const searchParams = new URLSearchParams()
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, String(value))
        }
      })
      url += `?${searchParams.toString()}`
    }

    return this.request<T>(url, { method: 'GET' })
  }

  async post<T>(endpoint: string, data?: any): Promise<ApiResponseWrapper<T>> {
    return this.request<T>(endpoint, { method: 'POST', body: data })
  }

  async put<T>(endpoint: string, data?: any): Promise<ApiResponseWrapper<T>> {
    return this.request<T>(endpoint, { method: 'PUT', body: data })
  }

  async patch<T>(endpoint: string, data?: any): Promise<ApiResponseWrapper<T>> {
    return this.request<T>(endpoint, { method: 'PATCH', body: data })
  }

  async delete<T>(endpoint: string): Promise<ApiResponseWrapper<T>> {
    return this.request<T>(endpoint, { method: 'DELETE' })
  }

  // File upload
  async uploadFile<T>(endpoint: string, file: File, additionalData?: Record<string, any>): Promise<ApiResponseWrapper<T>> {
    const formData = new FormData()
    formData.append('file', file)
    
    if (additionalData) {
      Object.entries(additionalData).forEach(([key, value]) => {
        formData.append(key, String(value))
      })
    }

    return this.request<T>(endpoint, { method: 'POST', body: formData })
  }

  // Note: Authorization methods removed - now using HTTP-only cookies
  // Cookies are automatically sent by the browser
}

// Custom API Error class
export class ApiError extends Error {
  public status: number
  public data?: any

  constructor(message: string, status: number, data?: any) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.data = data
  }
}

// Create API client instance
export const apiClient = new ApiClient(API_BASE_URL)

// Helper functions for common API patterns
export const api = {
  // Generic CRUD operations
  async getList<T>(endpoint: string, params?: Record<string, any>): Promise<PaginatedResponse<T>> {
    const response = await apiClient.get<PaginatedResponse<T>>(endpoint, params)
    return response.data
  },

  async getById<T>(endpoint: string, id: string): Promise<T> {
    const response = await apiClient.get<T>(`${endpoint}/${id}`)
    return response.data
  },

  async create<T>(endpoint: string, data: any): Promise<T> {
    const response = await apiClient.post<T>(endpoint, data)
    return response.data
  },

  async update<T>(endpoint: string, id: string, data: any): Promise<T> {
    const response = await apiClient.put<T>(`${endpoint}/${id}`, data)
    return response.data
  },

  async delete(endpoint: string, id: string): Promise<void> {
    await apiClient.delete(`${endpoint}/${id}`)
  },

  // Export data
  async exportData(endpoint: string, format: 'csv' | 'excel', params?: Record<string, any>): Promise<Blob> {
    const response = await fetch(`${API_BASE_URL}${endpoint}/export?format=${format}&${new URLSearchParams(params || {})}`, {
      credentials: 'include'
    })
    
    if (!response.ok) {
      throw new ApiError('Export failed', response.status)
    }
    
    return response.blob()
  }
}

// Response interceptor to handle auth token refresh
let isRefreshing = false
let failedQueue: Array<{ resolve: Function; reject: Function }> = []

const processQueue = (error: ApiError | null, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error)
    } else {
      resolve(token)
    }
  })
  
  failedQueue = []
}

// Intercept responses to handle token refresh
const originalRequest = apiClient.request.bind(apiClient)
apiClient.request = async function<T>(endpoint: string, options: RequestOptions = {}): Promise<ApiResponseWrapper<T>> {
  try {
    return await originalRequest(endpoint, options) as ApiResponseWrapper<T>
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      if (isRefreshing) {
        // Wait for token refresh
        return new Promise<ApiResponseWrapper<T>>((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        }).then(() => {
          return originalRequest(endpoint, options) as ApiResponseWrapper<T>
        })
      }

      isRefreshing = true

      try {
        // Attempt to refresh token
        const response = await originalRequest('/auth/admin/refresh', { method: 'POST' })
        
        if (response.data) {
          processQueue(null, (response.data as any).token || 'cookie-stored')
          return originalRequest(endpoint, options) as ApiResponseWrapper<T>
        }
      } catch (refreshError) {
        processQueue(refreshError as ApiError, null)
        // Don't redirect here - let the router handle navigation
        // This prevents conflicts between API interceptor and router guards
        console.log('Token refresh failed, authentication required')
        throw refreshError
      } finally {
        isRefreshing = false
      }
    }
    
    throw error
  }
}

/**
 * Simple fetch helper used by page components.
 * Accepts a pre-serialised body string (matches native fetch options),
 * adds auth cookies + JSON Content-Type, and returns the parsed JSON body.
 *
 * Unlike apiClient.request() this does NOT double-stringify the body —
 * callers that pass body: JSON.stringify({...}) are handled correctly.
 */
export async function apiFetch<T = any>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> | undefined)
  }

  const response = await fetch(url, {
    credentials: 'include',
    ...options,
    headers
  })

  if (!response.ok) {
    let message = response.statusText
    try {
      const errorBody = await response.json()
      message = errorBody?.message || message
    } catch {
      // ignore parse errors
    }
    throw new ApiError(message, response.status)
  }

  return response.json() as Promise<T>
}

export default apiClient
